import { Check, Clock, X } from "lucide-react";

/** Parse "7" / "3 " / 3 → number; null when blank/unparseable (untracked). */
function toQty(v: unknown): number | null {
  if (v == null) return null;
  const s = String(v).trim();
  if (!s || /^\{.*\}$/.test(s)) return null;
  const n = Number.parseInt(s, 10);
  return Number.isFinite(n) ? n : null;
}

/**
 * Availability pill shared by the product card and the buy box. Prefers the numeric quantity
 * ("Plus que N en stock" when low) and falls back to the stockStatus string. Brand green/amber/rose.
 */
export function StockBadge({
  quantity,
  stockStatus,
  size = "sm",
}: {
  quantity?: unknown;
  stockStatus?: string | null;
  size?: "sm" | "md";
}) {
  const qty = toQty(quantity);
  const status = (stockStatus ?? "").toString().trim();
  const isOut = status === "out-of-stock" || (qty != null && qty <= 0);
  const isOrder = status === "on-order";
  const isLow = !isOut && !isOrder && qty != null && qty > 0 && qty <= 5;

  const { label, cls, dot, icon } = isOut
    ? { label: "Rupture", cls: "bg-[#FBE9E9] text-[#B42318]", dot: "bg-[#B42318]", icon: <X className="h-3.5 w-3.5" /> }
    : isOrder
      ? { label: "Sur commande", cls: "bg-[#FFF4E0] text-[var(--color-amber-deep)]", dot: "bg-[var(--color-amber-deep)]", icon: <Clock className="h-3.5 w-3.5" /> }
      : isLow
        ? { label: `Plus que ${qty} en stock`, cls: "bg-[#FFF4E0] text-[var(--color-amber-deep)]", dot: "bg-[var(--color-amber-deep)]", icon: <Clock className="h-3.5 w-3.5" /> }
        : { label: "En stock", cls: "bg-[var(--color-forest-100)] text-[var(--color-forest-600)]", dot: "bg-[var(--color-forest-500)]", icon: <Check className="h-3.5 w-3.5" /> };

  const pad = size === "md" ? "px-3 py-1 text-sm" : "px-2.5 py-0.5 text-xs";
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full font-semibold ${pad} ${cls}`}>
      <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${dot}`} aria-hidden="true" />
      {size === "md" ? icon : null}
      {label}
    </span>
  );
}
